import decode from 'jwt-decode';

import config from 'utils/config';
import defer from 'utils/defer';

const expiryBuffer = 30;

export const parseQStr = ({ queryStr }) => {
  const searchParams = new URLSearchParams(queryStr);
  const defaultError = 'Failed to parse the result value of the authentication session';

  const error = searchParams.get('error');
  if (error) {
    throw new Error(searchParams.get('error_description') || defaultError);
  }

  const state = searchParams.get('state');
  if (!state) {
    throw new Error("'state' does not exist in the callback URL");
  }

  const code = searchParams.get('code');
  if (!code) {
    throw new Error("'code' does not exist in the callback URL");
  }

  return {
    code,
    state
  };
};

export const loginWithNextUrl = (nextUrl) => {
  window.sessionStorage.setItem('nextUrl', nextUrl);
  window.location.href = `${window.location.origin}/login`;
};

export const login = async (username, password) => {
  const response = await window.fetch(`${config('BFF_URL')}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config('API_GATEWAY_API_KEY')
    },
    body: JSON.stringify({
      username,
      password
    })
  });

  if (!response.ok) {
    throw new Error('Authentication Error: Failed to login');
  }

  const data = await response.json();

  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);

  return data;
};

const getAccessToken = () => {
  const accessToken = window.localStorage.getItem('accessToken');

  if (!accessToken) {
    return null;
  }

  const { exp } = decode(accessToken);
  const now = Math.ceil(new Date().getTime() / 1000) + expiryBuffer;

  if (exp < now) {
    return null;
  }

  return accessToken;
};

const logout = async () => {
  clearAccessToken();

  window.location.href = `${window.location.origin}/login`;
  return;
};

const refresh = async () => {
  const response = await window.fetch(`${config('BFF_URL')}/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config('API_GATEWAY_API_KEY')
    },
    body: JSON.stringify({
      refreshToken: window.localStorage.getItem('refreshToken')
    })
  });

  return response.ok ? response.json() : null;
};

export const clearAccessToken = () => {
  window.localStorage.removeItem('accessToken');
  window.localStorage.removeItem('refreshToken');
};

export const setAccessToken = (accessToken) => {
  window.localStorage.setItem('accessToken', accessToken);
};

export const setRefreshToken = (refreshToken) => {
  window.localStorage.setItem('refreshToken', refreshToken);
};

class ManagedToken {
  constructor() {
    this.pending = undefined;
    this.done = false;
  }

  async getToken() {
    if (this.pending && !this.done) {
      return this.pending;
    }

    const currentToken = getAccessToken();

    if (currentToken) {
      return currentToken;
    }

    const deferred = defer();
    this.done = false;
    this.pending = deferred.promise;

    const sessionData = await refresh();

    if (!sessionData) {
      deferred.resolve(null);
      this.done = true;
      return null;
    }

    const { accessToken, refreshToken } = sessionData;

    setAccessToken(accessToken);
    setRefreshToken(refreshToken);

    deferred.resolve(accessToken);
    this.done = true;

    return accessToken;
  }
}

const managedToken = new ManagedToken();

const authUtils = {
  parseQStr,
  loginWithNextUrl,
  logout,
  clearAccessToken,
  getAccessToken: () => managedToken.getToken(),
  refresh,
  login
};

export default authUtils;
